import React, { useState, useRef, useEffect } from 'react';
import { Search, Loader2, Sparkles, Save, SlidersHorizontal, X } from 'lucide-react';
import { Input } from '@/components/ui/input';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { useHybridSearch } from '@/hooks/useHybridSearch';
import { HybridSearchResult } from '@/types/search';
import { usePopularSearchSuggestions } from '@/hooks/usePopularSearchSuggestions';

interface HybridIntelligentSearchBarProps {
  onSearchChange: (value: string) => void;
  onToggleFilters: () => void;
  showFilters: boolean;
  onSelect?: (result: HybridSearchResult) => void;
  savedSearches?: string[];
  placeholder?: string;
}

export const HybridIntelligentSearchBar = ({
  onSearchChange,
  onToggleFilters,
  showFilters,
  onSelect,
  savedSearches = [],
  placeholder = 'Rechercher des membres...',
}: HybridIntelligentSearchBarProps) => {
  const {
    query,
    setQuery,
    results,
    isLoading,
    searchContext,
    contextualIcons,
    clearSearch,
  } = useHybridSearch();

  const { suggestions: popularSuggestions, isLoading: loadingSuggestions } = usePopularSearchSuggestions(8);
  const [showSuggestions, setShowSuggestions] = useState(false);
  const [filteredSuggestions, setFilteredSuggestions] = useState(popularSuggestions);
  const inputRef = useRef<HTMLInputElement>(null);

  // Filtrer les suggestions en fonction de la requête
  useEffect(() => {
    if (query.length > 0) {
      const filtered = popularSuggestions.filter((s) =>
        s.text.toLowerCase().includes(query.toLowerCase())
      );
      setFilteredSuggestions(filtered);
    } else {
      setFilteredSuggestions(popularSuggestions);
    }
  }, [query, popularSuggestions]);

  const handleQueryChange = (value: string) => {
    setQuery(value);
    onSearchChange(value);
    if (value.length > 0) {
      setShowSuggestions(true);
    }
  };

  const handleSelectResult = (result: HybridSearchResult) => {
    setQuery(result.member_name);
    setShowSuggestions(false);
    onSelect?.(result);
  };

  const handleSelectSuggestion = (suggestion: string) => {
    setQuery(suggestion);
    onSearchChange(suggestion);
    setShowSuggestions(false);
  };

  const handleSaveSearch = () => {
    if (query.trim()) {
      // TODO: Implémenter la sauvegarde
      console.log('Sauvegarder:', query);
    }
  };

  const handleClearSearch = () => {
    clearSearch();
    onSearchChange('');
    inputRef.current?.focus();
  };

  const getMatchTypeColor = (type: string) => {
    switch (type) {
      case 'excellent':
        return 'bg-success/10 text-success';
      case 'bon':
        return 'bg-primary/10 text-primary';
      case 'moyen':
        return 'bg-warning/10 text-warning';
      default:
        return 'bg-muted text-muted-foreground';
    }
  };

  return (
    <div className="relative w-full">
      {/* Input principal avec badges */}
      <div className="relative">
        <div className="absolute left-3 top-1/2 -translate-y-1/2 flex items-center gap-2">
          {searchContext ? (
            <span className="text-lg" title={searchContext.description}>
              {searchContext.icon}
            </span>
          ) : (
            <Search className="h-4 w-4 text-muted-foreground" />
          )}
        </div>

        <Input
          ref={inputRef}
          value={query}
          onChange={(e) => handleQueryChange(e.target.value)}
          onFocus={() => { if (query.length > 0) setShowSuggestions(true); }}
          placeholder={placeholder}
          className="pl-10 pr-32 h-11 bg-background border-border"
        />

        <div className="absolute right-3 top-1/2 -translate-y-1/2 flex items-center gap-2">
          {isLoading && <Loader2 className="h-4 w-4 animate-spin text-muted-foreground" />}
          
          {query && !isLoading && (
            <Button
              variant="ghost"
              size="icon"
              className="h-6 w-6"
              onClick={handleClearSearch}
            >
              <X className="h-3 w-3" />
            </Button>
          )}

          <Badge variant="outline" className="text-xs px-1.5 py-0.5">
            Recherche avancée
          </Badge>
        </div>
      </div>

      {/* Icônes contextuelles détectées */}
      {contextualIcons.length > 0 && (
        <div className="flex items-center gap-2 mt-2 flex-wrap">
          <span className="text-xs text-muted-foreground">Contexte détecté:</span>
          {contextualIcons.map((icon, idx) => (
            <Badge
              key={idx}
              variant="outline"
              className="gap-1.5 text-xs cursor-pointer hover:bg-accent transition-colors"
              title={icon.label}
            >
              <span>{icon.icon}</span>
              <span>{icon.label}</span>
              <span className="text-muted-foreground">
                ({Math.round(icon.matchScore)}%)
              </span>
            </Badge>
          ))}
        </div>
      )}

      {/* Dropdown avec résultats fuzzy et suggestions */}
      {showSuggestions && (
        <>
          <div className="fixed inset-0 z-40" onClick={() => setShowSuggestions(false)} />
          
          <div className="absolute top-full mt-2 w-full bg-background border rounded-lg shadow-lg z-50 max-h-[500px] overflow-hidden flex flex-col">
            {/* Résultats de recherche hybride */}
            {results.length > 0 && (
              <div className="border-b">
                <div className="px-3 py-2 bg-muted/50 text-xs font-medium text-muted-foreground">
                  🔍 Résultats ({results.length})
                </div>
                <div className="max-h-[300px] overflow-y-auto">
                  {results.map((result) => (
                    <button
                      key={result.member_id}
                      onClick={() => handleSelectResult(result)}
                      className="w-full text-left px-4 py-3 hover:bg-accent/50 transition-colors border-b last:border-b-0 focus:bg-accent focus:outline-none"
                    >
                      <div className="flex items-start justify-between gap-2">
                        <div className="flex-1 min-w-0">
                          <div className="flex items-center gap-2 mb-1">
                            <p className="font-medium truncate">{result.member_name}</p>
                            <Badge
                              variant="outline"
                              className={`text-xs ${getMatchTypeColor(result.match_type)}`}
                            >
                              <Sparkles className="h-2.5 w-2.5 mr-1" />
                              {Math.round(result.hybridScore * 100)}%
                            </Badge>
                          </div>
                          <div className="flex items-center gap-2 text-xs text-muted-foreground">
                            {result.member_company && (
                              <span className="truncate">{result.member_company}</span>
                            )}
                            {result.member_role && result.member_company && <span>•</span>}
                            {result.member_role && <span className="truncate">{result.member_role}</span>}
                          </div>
                          
                          {/* Icônes contextuelles du membre */}
                          {result.contextualIcons.length > 0 && (
                            <div className="flex gap-1 mt-1">
                              {result.contextualIcons.map((icon, idx) => (
                                <span
                                  key={idx}
                                  className="text-xs"
                                  title={icon.label}
                                >
                                  {icon.icon}
                                </span>
                              ))}
                            </div>
                          )}
                        </div>
                        <Badge variant="secondary" className="text-xs shrink-0">
                          {result.matched_field}
                        </Badge>
                      </div>
                    </button>
                  ))}
                </div>
              </div>
            )}

            {/* Message si aucun résultat */}
            {query.length >= 2 && results.length === 0 && !isLoading && (
              <div className="px-4 py-6 text-center text-sm text-muted-foreground">
                <p>Aucun résultat trouvé pour "{query}"</p>
                <p className="text-xs mt-1">Essayez avec un autre terme ou vérifiez l'orthographe</p>
              </div>
            )}

            {/* Suggestions populaires */}
            {(!query || filteredSuggestions.length > 0) && (
              <div>
                <div className="px-3 py-2 bg-muted/50 text-xs font-medium text-muted-foreground flex items-center gap-2">
                  💡 Suggestions {query ? 'similaires' : 'populaires'}
                  {loadingSuggestions && <Loader2 className="h-3 w-3 animate-spin" />}
                </div>
                <div className="max-h-[200px] overflow-y-auto">
                  {filteredSuggestions.slice(0, 5).map((suggestion, idx) => (
                    <button
                      key={idx}
                      onClick={() => handleSelectSuggestion(suggestion.text)}
                      className="w-full text-left px-4 py-2 hover:bg-accent/50 transition-colors border-b last:border-b-0 focus:bg-accent focus:outline-none"
                    >
                      <div className="flex items-center justify-between gap-2">
                        <div className="flex items-center gap-2 flex-1 min-w-0">
                          <span className="text-sm">{suggestion.icon}</span>
                          <span className="text-sm truncate">{suggestion.text}</span>
                        </div>
                        <Badge variant="outline" className="text-xs shrink-0">
                          {suggestion.popularity}%
                        </Badge>
                      </div>
                    </button>
                  ))}
                </div>
              </div>
            )}
          </div>
        </>
      )}

      {/* Actions */}
      <div className="flex items-center gap-2 mt-3">
        <Button
          variant="outline"
          size="sm"
          onClick={handleSaveSearch}
          disabled={!query.trim()}
          className="gap-1.5"
        >
          <Save className="h-3.5 w-3.5" />
          Sauvegarder
        </Button>
        <Button
          variant={showFilters ? 'default' : 'outline'}
          size="sm"
          onClick={onToggleFilters}
          className="gap-1.5"
        >
          <SlidersHorizontal className="h-3.5 w-3.5" />
          Filtres avancés
        </Button>
      </div>

      {/* Description du contexte */}
      {searchContext && (
        <p className="text-xs text-muted-foreground mt-2 flex items-center gap-1.5">
          <Sparkles className="h-3 w-3" />
          {searchContext.description}
        </p>
      )}
    </div>
  );
};
